import type { ClipPackage, GenMusic, MusicHighlightAnalysis, MusicVideoMode, MusicVideoPublishUse, MusicVideoSubtitleMode } from '~/services/musicService';

export const MUSIC_TO_MV_DRAFT_KEY = 'musicmint_mv_draft';
export const MUSIC_TO_MV_DRAFT_TTL_MS = 30 * 60 * 1000;
export const MUSIC_TO_MV_ROUTE = '/mv-generator?source=music_result&draft=1';
export const MUSIC_TO_MV_PENDING_ROUTE_KEY = 'musicmint_mv_pending_route';
export const MUSIC_TO_MV_IMPRESSION_KEY = 'musicmint_mv_impressions';
export const MUSIC_TO_MV_DEFAULT_CLIP_SECONDS = 10;
export const MUSIC_TO_MV_MIN_CLIP_SECONDS = 10;
export const MUSIC_TO_MV_MAX_CLIP_SECONDS = 60;

export type MusicToMvDraftSource = 'music_result' | 'generation_success';
export type MusicToMvAspectRatio = '9:16';
export type MusicToMvResolution = '540P';
export type MusicToMvReferenceSource = 'package_upload' | 'brand_profile' | 'song_cover' | 'generated_reference' | '';

export type MusicToMvDraft = {
  source: MusicToMvDraftSource;
  created_at: number;
  task_id: string;
  source_package_id?: string;
  music: GenMusic;
  defaults: {
    clip_start: number;
    clip_end: number;
    mode: Extract<MusicVideoMode, 'template'>;
    publish_use: Extract<MusicVideoPublishUse, 'vertical_promo'>;
    size: MusicToMvAspectRatio;
    resolution: MusicToMvResolution;
    template_id: 'realistic_story';
    subtitle_mode: Extract<MusicVideoSubtitleMode, 'copy' | 'none' | 'auto'>;
    images?: string[];
    reference_source?: MusicToMvReferenceSource | string;
    prompt_spec: {
      overall: string;
      style: string;
      narrative: 'medium';
      character: string;
      scene: string;
      extra: string;
    };
  };
};

export type BuildMusicToMvDraftInput = {
  source: MusicToMvDraftSource;
  taskId: string;
  music: GenMusic;
  requestedClipSeconds?: number;
  referenceImageIds?: string[];
  referenceSource?: MusicToMvReferenceSource | string;
  sourcePackageId?: string;
  now?: number;
};

type MusicToMvImpressionInput = {
  surface: 'song_card' | 'generation_success';
  taskId: string;
  musicId: string;
};

export type MusicToMvEnergyFrame = {
  time: number;
  energy: number;
  flux?: number;
};

export type MusicToMvClipSource = 'backend_highlight' | 'analysis' | 'energy' | 'fallback';

export type MusicToMvClipRecommendation = {
  start: number;
  end: number;
  source: MusicToMvClipSource;
};

const inMemoryImpressionKeys = new Set<string>();

const toFinitePositiveNumber = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
};

const roundClipSecond = (value: number) => Math.round(value * 10) / 10;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeRequestedClipSeconds = (value: unknown) => {
  const numeric = toFinitePositiveNumber(value);
  if (!numeric) return MUSIC_TO_MV_DEFAULT_CLIP_SECONDS;
  return roundClipSecond(clamp(numeric, MUSIC_TO_MV_MIN_CLIP_SECONDS, MUSIC_TO_MV_MAX_CLIP_SECONDS));
};

const normalizeReferenceImageIds = (ids?: string[]) => {
  if (!Array.isArray(ids)) return [];
  return [...new Set(ids.map((id) => String(id || '').trim()).filter(Boolean))].slice(0, 7);
};

export const resolveClipPackageMvReferenceDraft = (item?: Pick<ClipPackage, 'visual_assets' | 'candidates'> | null) => {
  const packageImages = normalizeReferenceImageIds(
    item?.visual_assets
      ?.filter((asset) => asset.use_for_mv)
      .sort((a, b) => (a.sort || 0) - (b.sort || 0))
      .map((asset) => asset.file_id)
  );

  if (packageImages.length > 0) {
    return { referenceImageIds: packageImages, referenceSource: 'package_upload' as const };
  }

  const hasSongCover = item?.candidates?.some((candidate) => Boolean(candidate.image_url?.trim()));
  if (hasSongCover) {
    return { referenceImageIds: [], referenceSource: 'song_cover' as const };
  }

  return { referenceImageIds: [], referenceSource: '' as const };
};

export const getMusicAudioUrl = (music?: Pick<GenMusic, 'audio_url' | 'audio_oss' | 'stream_audio_url'> | null) => {
  return music?.audio_oss || music?.stream_audio_url || music?.audio_url || '';
};

export const getMusicDuration = (music?: GenMusic | null) => {
  return toFinitePositiveNumber(music?.metadata?.duration)
    || toFinitePositiveNumber(music?.metadata?.highlight_analysis?.total_duration_seconds);
};

export const getDefaultMvClipEnd = (music?: GenMusic | null, requestedClipSeconds?: number) => {
  const duration = getMusicDuration(music);
  const targetSeconds = normalizeRequestedClipSeconds(requestedClipSeconds);
  if (duration > 0 && duration < MUSIC_TO_MV_MIN_CLIP_SECONDS) return 0;
  if (duration > 0) return roundClipSecond(Math.min(targetSeconds, duration));
  return targetSeconds;
};

export const isMusicEligibleForMvDraft = (music?: GenMusic | null) => {
  const duration = getMusicDuration(music);
  return Boolean(
    music?.id
    && getMusicAudioUrl(music)
    && (duration === 0 || duration >= MUSIC_TO_MV_MIN_CLIP_SECONDS)
  );
};

const truncate = (value: string, maxLength: number) => {
  const normalized = value.trim().replace(/\s+/g, ' ');
  return normalized.length > maxLength ? normalized.slice(0, maxLength).trim() : normalized;
};

const hasPromptData = (music: GenMusic) => {
  return Boolean(
    music.title?.trim()
    || music.metadata?.prompt?.trim()
    || music.metadata?.tags?.trim()
    || music.metadata?.gpt_description_prompt?.trim()
  );
};

const getMetadataArray = (metadata: Record<string, unknown> | undefined, keys: string[]) => {
  for (const key of keys) {
    const value = metadata?.[key];
    if (Array.isArray(value)) return value;
  }
  return [];
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === 'object';
};

const toFiniteNonNegativeNumber = (value: unknown) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : null;
};

const getSegmentNumberOrNull = (segment: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const numeric = toFiniteNonNegativeNumber(segment[key]);
    if (numeric !== null) return numeric;
  }
  return null;
};

const getSegmentNumber = (segment: Record<string, unknown>, keys: string[]) => {
  return getSegmentNumberOrNull(segment, keys) || 0;
};

const getSegmentScore = (segment: Record<string, unknown>) => {
  const rawScore = toFinitePositiveNumber(segment.score);
  const label = String(segment.label || segment.type || segment.section || '').toLowerCase();
  const labelBoost = /hook|chorus|drop|climax|highlight|peak|副歌|高潮/.test(label) ? 0.25 : 0;
  return rawScore + labelBoost;
};

const averageEnergy = (frames: MusicToMvEnergyFrame[]) => {
  if (!frames.length) return 0;
  return frames.reduce((sum, frame) => sum + frame.energy, 0) / frames.length;
};

const framesInRange = (frames: MusicToMvEnergyFrame[], start: number, end: number) => {
  return frames.filter((frame) => frame.time >= start && frame.time < end);
};

const buildRecommendationFromSegment = (
  segment: Record<string, unknown>,
  clipDuration: number,
  duration: number,
  source: MusicToMvClipSource
): MusicToMvClipRecommendation | null => {
  const rawStart = getSegmentNumberOrNull(segment, ['start', 'start_time', 'clip_start']);
  const rawEnd = getSegmentNumberOrNull(segment, ['end', 'end_time', 'clip_end']);
  const rawDuration = getSegmentNumberOrNull(segment, ['duration', 'clip_duration']);
  if (rawStart === null) return null;

  const segmentEnd = rawEnd ?? (rawDuration !== null ? rawStart + rawDuration : 0);
  if (segmentEnd <= rawStart || segmentEnd - rawStart < MUSIC_TO_MV_MIN_CLIP_SECONDS) return null;

  const maxStart = duration > 0 ? Math.max(0, duration - clipDuration) : rawStart;
  const start = roundClipSecond(clamp(rawStart, 0, maxStart));
  const end = roundClipSecond(Math.min(start + clipDuration, duration || start + clipDuration));
  if (end - start < MUSIC_TO_MV_MIN_CLIP_SECONDS) return null;

  return { start, end, source };
};

const pickSegmentRecommendation = (
  rawSegments: unknown[],
  clipDuration: number,
  duration: number,
  source: MusicToMvClipSource
) => {
  const candidates = rawSegments
    .filter(isRecord)
    .map((segment) => ({
      recommendation: buildRecommendationFromSegment(segment, clipDuration, duration, source),
      score: getSegmentScore(segment),
    }))
    .filter((candidate): candidate is { recommendation: MusicToMvClipRecommendation; score: number } => Boolean(candidate.recommendation))
    .sort((a, b) => b.score - a.score);

  return candidates[0]?.recommendation || null;
};

const getBackendHighlightRecommendation = (
  analysis: MusicHighlightAnalysis | undefined,
  clipDuration: number,
  duration: number
) => {
  if (analysis?.status !== 'succeeded') return null;

  if (isRecord(analysis.recommended)) {
    const recommended = buildRecommendationFromSegment(analysis.recommended, clipDuration, duration, 'backend_highlight');
    if (recommended) return recommended;
  }

  if (Array.isArray(analysis.segments)) {
    return pickSegmentRecommendation(analysis.segments, clipDuration, duration, 'backend_highlight');
  }

  return null;
};

export const findHighlightClipFromEnergy = (
  rawFrames: MusicToMvEnergyFrame[],
  requestedClipSeconds?: number,
  knownDuration?: number
): MusicToMvClipRecommendation | null => {
  const frames = rawFrames
    .filter((frame) => Number.isFinite(frame.time) && Number.isFinite(frame.energy) && frame.time >= 0)
    .sort((a, b) => a.time - b.time);
  const lastFrame = frames[frames.length - 1];
  const duration = toFinitePositiveNumber(knownDuration) || toFinitePositiveNumber(lastFrame?.time);
  const clipDuration = duration > 0
    ? Math.min(normalizeRequestedClipSeconds(requestedClipSeconds), duration)
    : normalizeRequestedClipSeconds(requestedClipSeconds);

  if (frames.length < 2 || clipDuration < MUSIC_TO_MV_MIN_CLIP_SECONDS) return null;

  let best: { start: number; score: number } | null = null;
  const maxStart = duration > 0 ? Math.max(0, duration - clipDuration) : lastFrame?.time || 0;

  for (const frame of frames) {
    const start = roundClipSecond(clamp(frame.time, 0, maxStart));
    const end = start + clipDuration;
    if (duration > 0 && end > duration + 0.25) continue;

    const windowFrames = framesInRange(frames, start, end);
    if (windowFrames.length < 2) continue;

    const currentEnergy = averageEnergy(windowFrames);
    const previousEnergy = averageEnergy(framesInRange(frames, Math.max(0, start - clipDuration), start));
    const flux = windowFrames.reduce((sum, item) => sum + toFinitePositiveNumber(item.flux), 0) / windowFrames.length;
    const lift = Math.max(0, currentEnergy - previousEnergy);
    const edgePenalty = start < 3 || (duration > 0 && end > duration - 3) ? 0.05 : 0;
    const score = currentEnergy + lift * 0.35 + flux * 0.15 - edgePenalty;

    if (!best || score > best.score || (score === best.score && start < best.start)) {
      best = { start, score };
    }
  }

  if (!best) return null;
  return {
    start: best.start,
    end: roundClipSecond(best.start + clipDuration),
    source: 'energy',
  };
};

const buildEnergyFramesFromAudioBuffer = (audioBuffer: AudioBuffer): MusicToMvEnergyFrame[] => {
  const frameSeconds = 0.5;
  const channel = audioBuffer.getChannelData(0);
  const frameSize = Math.max(1, Math.floor(audioBuffer.sampleRate * frameSeconds));
  const frames: MusicToMvEnergyFrame[] = [];
  let previousEnergy = 0;

  for (let offset = 0; offset < channel.length; offset += frameSize) {
    const end = Math.min(channel.length, offset + frameSize);
    let sumSquares = 0;
    for (let index = offset; index < end; index += 1) {
      sumSquares += channel[index] * channel[index];
    }
    const energy = Math.sqrt(sumSquares / Math.max(1, end - offset));
    frames.push({
      time: roundClipSecond(offset / audioBuffer.sampleRate),
      energy,
      flux: Math.max(0, energy - previousEnergy),
    });
    previousEnergy = energy;
  }

  return frames;
};

export const analyzeMusicHighlightFromAudioUrl = async (
  audioUrl: string,
  requestedClipSeconds?: number,
  knownDuration?: number
): Promise<MusicToMvClipRecommendation | null> => {
  if (typeof window === 'undefined' || !audioUrl) return null;
  const AudioContextCtor = window.AudioContext || (window as any).webkitAudioContext;
  if (!AudioContextCtor) return null;

  let audioContext: AudioContext | null = null;
  try {
    const response = await fetch(audioUrl, { mode: 'cors' });
    if (!response.ok) return null;
    const audioData = await response.arrayBuffer();
    audioContext = new AudioContextCtor();
    const audioBuffer = await audioContext.decodeAudioData(audioData.slice(0));
    const frames = buildEnergyFramesFromAudioBuffer(audioBuffer);
    return findHighlightClipFromEnergy(frames, requestedClipSeconds, knownDuration || audioBuffer.duration);
  } catch {
    return null;
  } finally {
    await audioContext?.close?.();
  }
};

export const recommendMusicToMvClip = (music: GenMusic, requestedClipSeconds?: number): MusicToMvClipRecommendation | null => {
  const duration = getMusicDuration(music);
  const clipDuration = getDefaultMvClipEnd(music, requestedClipSeconds);
  if (clipDuration < MUSIC_TO_MV_MIN_CLIP_SECONDS) return null;

  const fallback = {
    start: 0,
    end: clipDuration,
    source: 'fallback' as const,
  };

  const backendRecommendation = getBackendHighlightRecommendation(
    music.metadata?.highlight_analysis,
    clipDuration,
    duration
  );
  if (backendRecommendation) return backendRecommendation;

  const legacyRecommendation = pickSegmentRecommendation(
    getMetadataArray(music.metadata, ['highlight_segments', 'highlightSegments', 'sections', 'segments']),
    clipDuration,
    duration,
    'analysis'
  );
  if (legacyRecommendation) return legacyRecommendation;

  const energyRecommendation = findHighlightClipFromEnergy(
    getMetadataArray(music.metadata, ['energy_frames', 'energyFrames'])
      .filter((frame): frame is MusicToMvEnergyFrame => isRecord(frame) && 'time' in frame && 'energy' in frame),
    requestedClipSeconds,
    duration
  );
  if (energyRecommendation) return energyRecommendation;

  return fallback;
};

export const buildMusicToMvPromptSpec = (music: GenMusic, requestedClipSeconds?: number): MusicToMvDraft['defaults']['prompt_spec'] => {
  const title = music.title?.trim() || 'this song';
  const prompt = music.metadata?.prompt?.trim() || music.metadata?.gpt_description_prompt?.trim() || '';
  const tags = music.metadata?.tags?.trim() || '';
  const details = [prompt, tags ? `Style tags: ${tags}` : ''].filter(Boolean).join(' ');
  const clipSeconds = normalizeRequestedClipSeconds(requestedClipSeconds);

  return {
    overall: truncate(`Create a vertical ${clipSeconds}-second short music video for "${title}". ${details || 'Use the mood and rhythm of the song as the visual direction.'}`, 600),
    style: 'Short vertical social video for TikTok, Reels, and Shorts. Sync the visual rhythm to the song mood, keep the shots polished, energetic, and easy to share.',
    narrative: 'medium',
    character: '',
    scene: '',
    extra: '',
  };
};

export const buildMusicToMvDraft = ({
  source,
  taskId,
  music,
  requestedClipSeconds,
  referenceImageIds,
  referenceSource,
  sourcePackageId,
  now = Date.now(),
}: BuildMusicToMvDraftInput): MusicToMvDraft | null => {
  if (!taskId || !isMusicEligibleForMvDraft(music)) return null;
  const recommendation = recommendMusicToMvClip(music, requestedClipSeconds);
  if (!recommendation) return null;
  const images = normalizeReferenceImageIds(referenceImageIds);

  return {
    source,
    created_at: now,
    task_id: taskId,
    ...(sourcePackageId ? { source_package_id: sourcePackageId } : {}),
    music,
    defaults: {
      clip_start: recommendation.start,
      clip_end: recommendation.end,
      mode: 'template',
      publish_use: 'vertical_promo',
      size: '9:16',
      resolution: '540P',
      template_id: 'realistic_story',
      subtitle_mode: hasPromptData(music) ? 'copy' : 'none',
      ...(images.length > 0 ? { images } : {}),
      ...(referenceSource ? { reference_source: referenceSource } : {}),
      prompt_spec: buildMusicToMvPromptSpec(music, requestedClipSeconds),
    },
  };
};

export const isMusicToMvDraftFresh = (createdAt: number, now = Date.now()) => {
  return Number.isFinite(createdAt) && createdAt > 0 && now - createdAt <= MUSIC_TO_MV_DRAFT_TTL_MS;
};

const isDraftShapeValid = (draft: any): draft is MusicToMvDraft => {
  return Boolean(
    draft
    && (draft.source === 'music_result' || draft.source === 'generation_success')
    && typeof draft.created_at === 'number'
    && typeof draft.task_id === 'string'
    && (!('source_package_id' in draft) || typeof draft.source_package_id === 'string')
    && draft.task_id
    && draft.music
    && typeof draft.music.id === 'string'
    && getMusicAudioUrl(draft.music)
    && isMusicEligibleForMvDraft(draft.music)
    && draft.defaults
    && typeof draft.defaults.clip_start === 'number'
    && typeof draft.defaults.clip_end === 'number'
    && draft.defaults.clip_end > draft.defaults.clip_start
    && draft.defaults.clip_end - draft.defaults.clip_start >= MUSIC_TO_MV_MIN_CLIP_SECONDS
    && draft.defaults.mode === 'template'
    && draft.defaults.publish_use === 'vertical_promo'
    && draft.defaults.size === '9:16'
    && draft.defaults.resolution === '540P'
    && draft.defaults.template_id === 'realistic_story'
    && (
      draft.defaults.subtitle_mode === 'copy'
      || draft.defaults.subtitle_mode === 'auto'
      || draft.defaults.subtitle_mode === 'none'
    )
    && (!('images' in draft.defaults) || (
      Array.isArray(draft.defaults.images)
      && draft.defaults.images.length <= 7
      && draft.defaults.images.every((id: unknown) => typeof id === 'string' && id.trim())
    ))
    && (!('reference_source' in draft.defaults) || typeof draft.defaults.reference_source === 'string')
    && draft.defaults.prompt_spec
  );
};

export const parseMusicToMvDraft = (rawDraft: string | null | undefined, now = Date.now()): MusicToMvDraft | null => {
  if (!rawDraft) return null;

  try {
    const parsed = JSON.parse(rawDraft);
    if (!isDraftShapeValid(parsed)) return null;
    if (!isMusicToMvDraftFresh(parsed.created_at, now)) return null;
    const duration = getMusicDuration(parsed.music);
    if (duration >= MUSIC_TO_MV_MIN_CLIP_SECONDS && parsed.defaults.clip_end > duration) {
      parsed.defaults.clip_end = duration;
    }
    if (parsed.defaults.subtitle_mode === 'auto') {
      parsed.defaults.subtitle_mode = 'copy';
    }
    return parsed;
  } catch {
    return null;
  }
};

export const saveMusicToMvDraft = (draft: MusicToMvDraft) => {
  if (typeof window === 'undefined') return false;
  window.sessionStorage.setItem(MUSIC_TO_MV_DRAFT_KEY, JSON.stringify(draft));
  return true;
};

export const readMusicToMvDraft = (now = Date.now()) => {
  if (typeof window === 'undefined') return null;
  return parseMusicToMvDraft(window.sessionStorage.getItem(MUSIC_TO_MV_DRAFT_KEY), now);
};

export const clearMusicToMvDraft = () => {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(MUSIC_TO_MV_DRAFT_KEY);
};

export const markMusicToMvPendingRoute = () => {
  if (typeof window === 'undefined') return false;
  window.sessionStorage.setItem(MUSIC_TO_MV_PENDING_ROUTE_KEY, '1');
  return true;
};

export const consumeMusicToMvPendingRoute = () => {
  if (typeof window === 'undefined') return false;
  const hasPendingRoute = window.sessionStorage.getItem(MUSIC_TO_MV_PENDING_ROUTE_KEY) === '1';
  window.sessionStorage.removeItem(MUSIC_TO_MV_PENDING_ROUTE_KEY);
  return hasPendingRoute;
};

export const getMusicToMvDraftAnalytics = (draft: MusicToMvDraft) => ({
  source: draft.source,
  task_id: draft.task_id,
  source_package_id: draft.source_package_id || '',
  music_id: draft.music.id,
  duration: getMusicDuration(draft.music),
  has_lyrics_or_prompt: hasPromptData(draft.music),
  clip_duration: Math.max(0, draft.defaults.clip_end - draft.defaults.clip_start),
  publish_use: draft.defaults.publish_use,
  mode: draft.defaults.mode,
  resolution: draft.defaults.resolution,
  image_count: draft.defaults.images?.length || 0,
  reference_source: draft.defaults.reference_source || '',
});

const readSessionImpressionKeys = () => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(MUSIC_TO_MV_IMPRESSION_KEY);
    return new Set<string>(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set<string>();
  }
};

export const shouldTrackMusicToMvImpression = ({ surface, taskId, musicId }: MusicToMvImpressionInput) => {
  if (!surface || !taskId || !musicId) return false;
  const key = `${surface}:${taskId}:${musicId}`;
  const sessionKeys = readSessionImpressionKeys();
  const keys = sessionKeys || inMemoryImpressionKeys;
  if (keys.has(key)) return false;
  keys.add(key);
  if (sessionKeys && typeof window !== 'undefined') {
    window.sessionStorage.setItem(MUSIC_TO_MV_IMPRESSION_KEY, JSON.stringify([...keys]));
  }
  return true;
};
